import type { Ticket, TicketStatus, Counters, LevelGroup, EligibilityInfo, TicketAction, AggStatus, JourneyStatus } from './types'
import { env } from '../config/env'

// ============================================================
// Engine de elegibilidade — porta de app/data.jsx.
// Regra de ouro: `podeIniciar` é a fonte da verdade visual.
//
// No MockDataSource este motor É a verdade. No RealDataSource a verdade
// vem do servidor (ticket.podeIniciar/blockReason/nextAvailableTicketIds);
// `resolveEligibility` escolhe a fonte — com a API real, o ramo do motor
// nunca executa.
// ============================================================

const PENDING: TicketStatus[]     = ['Opened', 'Authorized', 'Confirmed', 'Checked', 'Reopened']
const IN_PROGRESS: TicketStatus[] = ['Called', 'Started']
const RESOLVED: TicketStatus[]    = ['Completed', 'Canceled'] // "resolvido" libera o próximo nível

export const isPending    = (s: TicketStatus): boolean => PENDING.includes(s)
export const isInProgress = (s: TicketStatus): boolean => IN_PROGRESS.includes(s)
export const isResolved   = (s: TicketStatus): boolean => RESOLVED.includes(s)

// Todos os níveis anteriores estão resolvidos?
function levelResolved(tickets: Ticket[], level: number): boolean {
  return tickets.filter((t) => t.sequence < level).every((t) => isResolved(t.status))
}
// Quais tickets do mesmo nível estão ocupando o slot (um por vez)?
function levelBusy(tickets: Ticket[], level: number): Ticket[] {
  return tickets.filter((t) => t.sequence === level && isInProgress(t.status))
}

/** Fonte da verdade local (mock). */
export function computeTicket(tickets: Ticket[], ticket: Ticket): EligibilityInfo {
  if (!isPending(ticket.status)) {
    return { podeIniciar: false, motivo: null, bloqueadoPor: [], razao: null }
  }
  if (!levelResolved(tickets, ticket.sequence)) {
    const pend = tickets.filter((t) => t.sequence < ticket.sequence && !isResolved(t.status))
    return {
      podeIniciar: false,
      motivo: 'Aguardando conclusão da etapa anterior',
      bloqueadoPor: pend.map((t) => t.ticketId),
      razao: 'nivel',
    }
  }
  const busy = levelBusy(tickets, ticket.sequence)
  if (busy.length) {
    return {
      podeIniciar: false,
      motivo: 'Outro serviço deste nível está em atendimento',
      bloqueadoPor: busy.map((t) => t.ticketId),
      razao: 'concorrente',
    }
  }
  return { podeIniciar: true, motivo: null, bloqueadoPor: [], razao: null }
}

/**
 * Seam mock↔real: se o servidor já respondeu `podeIniciar`, usa-o;
 * senão cai no motor local. As telas chamam SEMPRE esta função.
 */
export function resolveEligibility(tickets: Ticket[], ticket: Ticket): EligibilityInfo {
  // singleDay: o servidor ainda manda `podeIniciar:false` por "sessão vencida", regra que não
  // vale mais no mesmo dia. O desvio fica aqui, e não em cada tela, senão todo consumidor de
  // resolveEligibility precisa repetir a checagem por conta (e alguns esquecem).
  if (ticket.podeIniciar !== undefined && !isOverdueButToday(ticket)) {
    return {
      podeIniciar: ticket.podeIniciar,
      motivo: ticket.motivoBloqueio ?? null,
      bloqueadoPor: ticket.bloqueadoPor ?? [],
      razao: ticket.razao ?? null,
    }
  }
  return computeTicket(tickets, ticket)
}

// Estágio da jornada = estado da etapa MAIS ATRASADA ainda não resolvida (a "frente"/gargalo).
// Rank menor = mais atrás no workflow; só vale para tickets não-resolvidos.
const STAGE_RANK: Partial<Record<TicketStatus, number>> = {
  Opened: 1, Authorized: 1,        // Agendada
  Confirmed: 2,                    // Confirmada
  Checked: 3, Reopened: 3,         // Aguardando chamada
  Called: 4,                       // Chamado (chamado, ainda não iniciado)
  Started: 5,                      // Em atendimento
}
const STAGE_OF: Record<number, AggStatus> = {
  1: 'Agendada', 2: 'Confirmada', 3: 'AguardandoChamada', 4: 'Chamado', 5: 'EmAtendimento',
}

// Status consolidado do agregador, calculado pela frente do workflow (a UI só consome).
export function deriveAggStatus(tickets: Ticket[]): AggStatus {
  if (!tickets.length) {return 'Aguardando'}
  const active = tickets.filter((t) => !isResolved(t.status))
  if (!active.length) {
    if (tickets.every((t) => t.status === 'Canceled')) {return 'Cancelado'}
    return tickets.some((t) => t.status === 'Canceled') ? 'ParcialmenteCompleto' : 'Completo'
  }
  // frente = nível mais cedo ainda não resolvido (o gargalo atual); níveis posteriores são "futuro".
  // estágio = ticket mais avançado desse nível (se algo já está em atendimento, a frente está nele).
  const frontLevel = Math.min(...active.map((t) => t.sequence))
  const frontRank = Math.max(...active.filter((t) => t.sequence === frontLevel).map((t) => STAGE_RANK[t.status] ?? 1))
  return STAGE_OF[frontRank] ?? 'AguardandoChamada'
}

export function journeyStatus(status: JourneyStatus | undefined, tickets: Ticket[]): JourneyStatus {
  if (status) {return status}
  const derived = deriveAggStatus(tickets)
  if (derived === 'Completo') {return 'Completed'}
  if (derived === 'ParcialmenteCompleto') {return 'PartiallyCompleted'}
  if (derived === 'Cancelado') {return 'Canceled'}
  if (derived === 'EmAtendimento' || derived === 'Chamado') {return 'InProgress'}
  return 'Waiting'
}

export function journeyDisplayStatus(status: JourneyStatus | undefined, tickets: Ticket[]): AggStatus {
  const current = journeyStatus(status, tickets)
  if (current === 'Completed') {return 'Completo'}
  if (current === 'PartiallyCompleted') {return 'ParcialmenteCompleto'}
  if (current === 'Canceled') {return 'Cancelado'}
  // Jornada ativa (Waiting/InProgress): o estágio fino vem dos steps, não do status grosso do
  // backend. Um step Checked é 'AguardandoChamada' — nem 'Agendada' (sem check-in) nem
  // 'EmAtendimento' (só quando chamado/iniciado). O backend não recomputa a jornada na transição
  // do step (updatedAt fica congelado), então derivamos aqui pra badge/abas refletirem o check-in.
  return deriveAggStatus(tickets)
}

export function isActiveJourney(status: JourneyStatus | undefined, tickets: Ticket[]): boolean {
  const displayStatus = journeyDisplayStatus(status, tickets)
  return displayStatus !== 'Completo'
    && displayStatus !== 'ParcialmenteCompleto'
    && displayStatus !== 'Cancelado'
}

export function counters(tickets: Ticket[]): Counters {
  return {
    total:         tickets.length,
    aguardando:    tickets.filter((t) => isPending(t.status)).length,
    emAtendimento: tickets.filter((t) => isInProgress(t.status)).length,
    completos:     tickets.filter((t) => t.status === 'Completed').length,
    cancelados:    tickets.filter((t) => t.status === 'Canceled').length,
  }
}

// Níveis agrupados, em ordem, para a timeline
export function groupByLevel(tickets: Ticket[]): LevelGroup[] {
  const levels = [...new Set(tickets.map((t) => t.sequence))].sort((a, b) => a - b)
  return levels.map((lvl) => ({
    sequence: lvl,
    tickets: tickets
      .filter((t) => t.sequence === lvl)
      .sort((a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER) || a.ticketId - b.ticketId),
  }))
}

// IDs dos tickets que podem ser chamados agora
export function proximosDisponiveis(tickets: Ticket[]): number[] {
  return tickets.filter((t) => resolveEligibility(tickets, t).podeIniciar).map((t) => t.ticketId)
}

/**
 * Ações permitidas para um bilhete. Seam server-driven (igual ao de elegibilidade):
 * se o servidor mandou `allowedActions`, ela é a verdade; senão, computa da TABELA
 * CENTRAL abaixo (única fonte das regras — evita heurística espalhada na UI).
 * Regra de reabrir: só em `Canceled` (reinserir) — NUNCA em `Completed`.
 */
const ONE_DAY_MS = 24 * 60 * 60 * 1000

function isStaleOverdue(ticket: Ticket): boolean {
  const overdueSince = ticket.sessionEnd ?? ticket.sessionStart
  return overdueSince != null && Date.now() - new Date(overdueSince).getTime() > ONE_DAY_MS
}

// Vencida hoje: segue chamável normalmente (backoffice legado). Só passa a bloquear depois
// de 1 dia — aí `isStaleOverdue` assume e libera o reagendamento. Exige data de sessão
// conhecida: sem ela não dá para provar que é de hoje, e destravar no escuro é pior que
// manter o bloqueio (o operador ainda tem o Reagendar como saída).
export function isOverdueButToday(ticket: Ticket): boolean {
  const overdueSince = ticket.sessionEnd ?? ticket.sessionStart
  return env.singleDay && !!ticket.isOverdue && overdueSince != null && !isStaleOverdue(ticket)
}

export function actionsFor(tickets: Ticket[], ticket: Ticket): Set<TicketAction> {
  // singleDay: sessão vencida no mesmo dia segue o comportamento do backoffice legado —
  // continua chamável normalmente. Ignora allowedActions do servidor pra esse caso
  // específico (o servidor ainda pensa em termos de "sessão vencida = bloqueado").
  const overdueButToday = isOverdueButToday(ticket)

  if (ticket.allowedActions && !overdueButToday) {
    const allowed = new Set(ticket.allowedActions)
      if (ticket.status === 'Confirmed') {
      allowed.delete('confirm')
      allowed.delete('call')
      allowed.add('check')
    }
    if (ticket.status === 'Called') {
      allowed.delete('call')
      // Called sempre pode iniciar: cobre o estado otimista (chamada em voo, o
      // servidor ainda não devolveu 'start' em allowedActions) e o estado real.
      allowed.add('start')
      if ((ticket.timesCalled ?? 0) >= 3) {
        allowed.delete('recall')
        allowed.add('reopen')
        // Espelha o filazero-app: junto do reopen, o card chamado libera o
        // "Reinserir" (puta no fim da fila com nova previsão — modal confirm).
        allowed.add('reinsert')
      } else {
        allowed.add('recall')
      }
    }
    if (ticket.status === 'Started') {
      // Started otimista: o servidor ainda não devolveu 'complete' em allowedActions
      // (o card ainda carrega o set do estado Called). Um ticket em curso sempre
      // pode ser concluído/transferido — cobre "Concluir" e "Concluir e editar".
      allowed.delete('start')
      allowed.delete('recall')
      allowed.delete('reopen')
      allowed.delete('call')
      allowed.add('complete')
      allowed.add('transfer')
    }
    return allowed
  }

  const set = new Set<TicketAction>()
  const st = ticket.status
  if (ticket.isOverdue && !overdueButToday) {
    // Reagendar só libera 1 dia após o vencimento da sessão — no dia em que vence ainda
    // pode ser chamado normalmente (cai no fluxo comum abaixo).
    // Se o backend marcou scheduleState='Expired', também libera reagendamento.
    if (isStaleOverdue(ticket) || ticket.scheduleState !== 'Expired') {set.add('reschedule')}
    return set
  }
  if (isResolved(st)) {
    if (st === 'Canceled') {set.add('reopen')} // reinserir; resolvido-por-cancelamento
    return set
  }
  if (st === 'Started') { set.add('complete'); set.add('transfer'); return set }
  if (st === 'Called') {
    set.add('start')
    if ((ticket.timesCalled ?? 0) >= 3) { set.add('reopen'); set.add('reinsert') } else { set.add('recall') }
    set.add('transfer')
    return set
  }
  // pendente (Opened/Authorized/Confirmed/Checked/Reopened)
  if (st === 'Reopened') { set.add('confirm'); set.add('transfer'); return set }
  if (st === 'Confirmed') { set.add('check'); set.add('transfer'); return set }
  const eligibility = resolveEligibility(tickets, ticket)
  if (eligibility.podeIniciar) {set.add('call')}
  if (st === 'Opened' || st === 'Authorized') {set.add('confirm')}
  set.add('transfer')
  return set
}

export function elapsedUrgency(startedAt?: number, estMin?: number): 'neutral' | 'warning' | 'danger' {
  if (!startedAt || !estMin || estMin <= 0) {return 'neutral'}
  const elapsedMs = Date.now() - startedAt
  if (elapsedMs > estMin * 2 * 60_000) {return 'danger'}
  if (elapsedMs > estMin * 60_000) {return 'warning'}
  return 'neutral'
}
